import { ref, computed, inject, reactive, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import {useRouter, useRoute, onBeforeRouteLeave} from 'vue-router';

export function useCommonComposable() {
    const notify = inject('notify');

    // Объявляем реактивные переменные
    const serverURL = ref('');
    const sizes = reactive({
        XS: 'X-Small',
        S: 'Small',
        M: 'Medium',
        L: 'Large',
        XL: 'X-Large',
        XXL: 'XX-Large'
    });

    const user = ref([]);

    const categories = ref([]);

    const store = useStore();
    const router = useRouter();

    // Обработчики жизненного цикла
    onMounted(() => {
        serverURL.value = process.env.VUE_APP_SERVER_URL.replace(/\\/g, '/');
    });

    const isMobile = computed(() => {
        return window.innerWidth <= 750
    })

    // Методы
    const getUser = async (notStrict) => {
        const cookies = getCookie();
        if (!cookies || !cookies.authToken) {
            if (!notStrict) {
                notify('Для просмотра аккаунта необходимо авторизоваться');
                await router.replace('/auth/login');
            }
            user.value = null;
            return;
        }

        await store.dispatch('user/fetchActualUser');

        user.value = JSON.parse(JSON.stringify(store.state.user.actualUser))
        if (!user.value[0] || !user.value[0].ID) {
            if (!notStrict) {
                store.state.user.actualUser = []
                notify('Для просмотра аккаунта необходимо авторизоваться')
                await router.replace({path: '/auth/login'})
            }
            user.value = null;
        }
    };

    const getDiscountValue = (base_price, sale_price) => {
        const discount = (Math.round((1 - (sale_price / base_price)) * 100))
        return discount > 10 ? '–' + discount + '%' : ''
    }

    const setImageURL = (obj) => {
        if (obj) {
            try {
                return `${serverURL.value}/${obj['Ссылка'].replace(/\\/g, '/')}`;
            } catch (e) {
                return '/';
            }
        } else {
            return '/';
        }
    };

    const setImageThumbURL = (obj) => {
        if (obj) {
            try {
                let link = obj['Ссылка'];
                // link = link.replace(/\.jpeg$/i, '.webp');
                // link = link.replace(/\.png$/i, '.webp');
                link = serverURL.value + '/' + link.replace(/\\/g, '/');
                link = link.replace(/\/uploads\//g, "/uploads/thumbnails/");

                return link
            } catch (e) {
                return '/'
            }
        } else {
            return '/'
        }
    };

    const rgbToHex = (r, g, b) => {
        return `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;
    };

    const getCaseByNumeral = (num, word) => {
        if (num > 20) {
            const lastDigit = String(num)[String(num).length - 1];
            switch (lastDigit) {
                case '1': return word;
                case '2':
                case '3':
                case '4': return word + 'а';
                default: return word + 'ов';
            }
        } else {
            switch (num) {
                case 1: return word;
                case 2:
                case 3:
                case 4: return word + 'а';
                default: return word + 'ов';
            }
        }
    };

    const handleInternalClick = (event) => {
        event.preventDefault();
    };

    const convertStringToDate = (str) => {
        const parts = str.split('.');
        return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    };

    const getCookie = () => {
        return document.cookie.split('; ')
            .reduce((acc, item) => {
                const pair = item.split('='); // Делим пару на ключ и значение

                // Проверяем, что получилось ровно два элемента
                if (pair.length === 2) {
                    const [name, value] = pair;
                    acc[name.trim()] = value.trim(); // Записываем только валидные пары
                }

                return acc;
            }, {});
    };

    const getCategories = async (names) => {
        if (names) {
            for (let name of names) {
                await store.dispatch('category/fetchCategoryByName', name)
            }
            await store.dispatch("category/fetchCategoryItems");
            categories.value = store.state.category.categories
        } else {
            await store.dispatch("category/fetchCategories");
            await store.dispatch("category/fetchCategoryItems");
            categories.value = store.state.category.categories;
        }
    }

    const removeTooltips = () => {
        const tooltipElems = document.querySelectorAll('.tooltip');
        if (tooltipElems) {
            for (let tooltipElem of tooltipElems) {
                document.body.removeChild(tooltipElem);
            }
        }
    }

    /**
     * Анимация печати текста
     */
    const useTypingAnimation = (
        text,
        {
            typingSpeed = 40,
            restartDelay = 5000,
            pauseBeforeRestart = 350,
            maxLength = null
        } = {}
    ) => {
        const typedText = ref('');

        let destroyed = false;
        let timeoutId = null;
        let currentIndex = 0;

        const wait = (ms) =>
            new Promise(resolve => {
                timeoutId = setTimeout(resolve, ms);
            });

        const truncateText = (value) => {
            if (maxLength === null || value.length <= maxLength) {
                return value;
            }

            return value.slice(0, maxLength - 3) + '...';
        };

        const getTexts = () => {

            const value = typeof text === 'function'
                ? text()
                : text;

            const texts = Array.isArray(value)
                ? value
                : [value];

            return texts.map(truncateText);
        };

        const start = async () => {
            while (!destroyed) {

                const texts = getTexts();

                // Если массив пустой — просто ждем
                if (!texts.length) {
                    await wait(restartDelay);
                    continue;
                }

                const value = texts[currentIndex];

                for (let i = 0; i <= value.length && !destroyed; i++) {
                    typedText.value = value.slice(0, i);
                    await wait(typingSpeed);
                }

                // Если нужно напечатать только один раз
                if (restartDelay === 'none') {

                    // Если есть еще тексты — переходим к следующему
                    if (currentIndex < texts.length - 1) {
                        await wait(pauseBeforeRestart);

                        currentIndex++;

                        continue;
                    }

                    // Последний текст напечатан — завершаем
                    break;
                }

                await wait(restartDelay);

                typedText.value = '';

                await wait(pauseBeforeRestart);

                currentIndex = (currentIndex + 1) % texts.length;
            }
        };

        const stop = () => {
            destroyed = true;
            clearTimeout(timeoutId);
        };

        const restart = () => {
            stop();

            destroyed = false;

            start();
        };

        onMounted(start);
        onUnmounted(stop);

        return {
            typedText,
            start,
            stop,
            restart
        };
    };

    // Возвращаем доступные объекты и методы
    return {
        user,
        serverURL,
        sizes,
        categories,
        isMobile,
        getUser,
        setImageURL,
        setImageThumbURL,
        rgbToHex,
        getCaseByNumeral,
        handleInternalClick,
        convertStringToDate,
        getCookie,
        getCategories,
        removeTooltips,
        useTypingAnimation,
        getDiscountValue
    };
}